import { useMemo } from "react";
import destinationPassionMap from "../data/destinations.json";

/**
 * Hook to get a destination based on user's passions for the Passions Hub
 * @param {string[]} passions - Array of passion keys (e.g., ['beach', 'golf'])
 * @param {boolean} loading - Whether passions are still loading
 * @returns {Object} - Destination data with image, city, description, etc.
 */
export const usePassionsHubDestination = (passions, loading = false) => {
  const destination = useMemo(() => {
    // Return null if still loading or no passions
    if (loading || !passions || passions.length === 0) {
      return null;
    }

    // Try to find a destination from the user's passions
    for (const passion of passions) {
      const destinations = destinationPassionMap[passion];
      if (destinations && destinations.length > 0) {
        // Get a destination from this passion category
        // Use the first one which is typically highest ranked
        const dest = destinations[0];
        
        return {
          city: dest.city,
          country: dest.country,
          imageUrl: dest.imageUrl,
          pageUrl: dest.pageUrl,
          headline: dest.headline,
          description: dest.description,
          passion: passion,
          // Include scores for filtering/display purposes
          scores: {
            beach: parseFloat(dest.Beach || 0),
            culinary: parseFloat(dest.Culinary || 0),
            golf: parseFloat(dest.Golf || 0),
            ski: parseFloat(dest.Ski || 0),
            spa: parseFloat(dest.Spa || 0),
            culture: parseFloat(dest.Culture || 0),
            music: parseFloat(dest.Music || 0),
          },
        };
      }
    }

    return null;
  }, [passions, loading]);

  // Format destination name for display
  const destinationName = useMemo(() => {
    if (!destination) return null;
    return destination.country 
      ? `${destination.city}, ${destination.country}`
      : destination.city;
  }, [destination]);

  return {
    destination,
    destinationName,
    imageUrl: destination?.imageUrl || null,
    headline: destination?.headline || null,
    description: destination?.description || null,
    loading,
  };
};
